import Backbone from 'backbone'
import template from './paste-destination-form.hbs'
import ObjectSelector from 'scoreboard/components/object-selector'
import _ from 'lodash'
import defaultPasteAllowed, {
	domainTypeUsesReplication,
	testMixingReplication
} from './default-paste-allowed'
import isInSubtree from 'scoreboard/components/is-in-subtree'
import FormView from 'scoreboard/components/form-view'
import i18n from 'scoreboard/components/i18n'
import config from 'scoreboard/components/config'
import { SIBLING } from 'scoreboard/components/gardener'
import resizeDialogContent from 'scoreboard/components/resize-dialog-content'

function isLastChild(tree, nodeId, candidateId) {
	var children = tree.children(candidateId)
	if (!children || children.length === 0) {
		return false
	}
	return children[children.length - 1].id == nodeId
}

/**
 * Superclass view used when pasting an non-organization domain object; extensions exist for a standard copy/move and a template copy.
 * Gives the user an org tree and domain object tree to choose the parent under which the new copy will live.
 */
var PasteDestinationForm = Backbone.View.extend({
	tagName: 'form',
	className: 'paste-destination-form',

	/**
	 * Returns whether paste is allowed on the given node according to business
	 * rules for the particular domain object being pasted.  Note rules about
	 * not moving an object inside its own subtree are always enforced.  If
	 * moving to a new organization, parent will be null.  The most common rules
	 * are for dashboards, documents and reports so we'll implement those here.
	 */
	pasteAllowed: defaultPasteAllowed,

	// dialog title/header (can be string or function)
	title: null,

	// array of banner messages to display at the top (optional)
	messages: null,

	// function to call when form is submitted (validation is done first)
	submit: null,

	/**
	 * @param options
	 *
	 * @param {String} options.mode either 'move' or 'copy'
	 *
	 * @param {String} options.section the type of object to be selected
	 *
	 * @param {Object} options.object the object being moved or copied, which must
	 * have ID and label properties
	 *
	 * @param {Object} [objectSelector] options that will be passed along to
	 * the object-selector
	 */
	initialize: function(options = {}) {
		this.options = options
		const {objectSelector = {}} = this.options

		let selectorOptions = _.extend({
			section: _.get(this.options, 'section'),
			id: _.get(this.options, 'parentId'),
			projectId: this.options.projectId,
			// I think this is only relevant to scorecard items, but it's a lot
			// less code to just put it here
			objectParams: { pendingDeletion: true }
		}, _.cloneDeep(objectSelector))

		this._sourceOrganizationId = _.get(this.options.object, 'organizationId', null)
		let repEnabled = config.replicationInputEnabled || config.replicationOutputEnabled
		if (repEnabled && domainTypeUsesReplication(this.options.domainType)) {
			//#21955 if we're replicated, add check to see if targets are legit
			selectorOptions.objectParams = { 'preventMixedReplication' : this._sourceOrganizationId }
		}
		this.objectSelector = new ObjectSelector(selectorOptions)
		this.objectSelector.on('select success empty', this.onSelectObject.bind(this))

		if (options && options.pasteAllowed) {
			this.pasteAllowed = options.pasteAllowed
		}

		var PasteFormView = FormView.extend({
			test: this.test.bind(this)
		})

		this.formView = new PasteFormView({
			el: this.el,
			submit: this.submit.bind(this),
			// #28726 some implementations (like template-destination-form) don't
			// actually have a "secondary submit" button, just "Next"
			submitSecondary: this.submitSecondary ? this.submitSecondary.bind(this) : null,
			allowCleanSubmission: true
		})

		// getSourceOrg uses the org tree, which won't always contain the source
		// org if switching between projects. So grab the org when first rendered
		this.objectSelector.once('org:tree:rendered', () => {
			this.sourceOrganization = this.getSourceOrg()
		})
	},

	onSelectObject: function() {
		this.formView.clearErrors()
		this.formView.change()
	},

	/**
	 * Returns the org the source object comes from if it has been loaded
	 * in the org tree. If it hasn't been loaded, returns nil
	 */
	getSourceOrg: function() {
		return _.invoke(this.objectSelector, 'orgGardener.tree.get', this._sourceOrganizationId)
	},

	/**
	 * Returns the destination organization for the paste
	 */
	getOrg: function() {
		return _.result(this.objectSelector, 'orgGardener.tree.selected')
	},

	/**
	 * Returns the new parent for the node being pasted. This will be null
	 * if pasting to a top-level org. This will be nil if the object tree hasn't
	 * loaded yet
	 */
	getParent: function() {
		if (_.has(this.objectSelector, 'objectGardener.tree')) {
			let tree = this.objectSelector.objectGardener.tree
			if (tree.selected()) {
				return tree.selected()
			}
			// #31510 if we're not working with a forest
			if (!tree.options.forest) {
				// #31493 if nothing selected we use the root
				return tree.get(_.result(tree, 'root.id'))
			}
		}
		return null // no parent found
	},

	/**
	 * See Gardener.getAddPosition()
	 */
	addPosition: function () {
		let parentId = _.get(this.getParent(), 'id')
		let parentType = _.get(this.getParent(), 'type')
		let gardener = this.objectSelector.objectGardener

		// #31253 when a folder is pasted on another folder, it should be a
		// child instead of a sibling. This is different than the rules for
		// adding a new folder (in which case it should be a sibling instead of
		// a child #7476)
		if (parentType == 'folder' && this.options.object.type == 'folder') {
			return { parentId }
		}

		// #29915 if nothing's selected, we're moving to the top level
		if (!parentId) {
			// we don't want to specify an index or a parent. The proper org ID
			// is already specified in the "submit" method, so not repeating it
			// here.
			return { }
		}

		// #31338
		if (this.options.domainType === 'slide') {
			return { parentId }
		}

		return gardener.getAddPosition(this.options.object, parentId)
	},

	test: function () {
		let error
		let org = this.objectSelector.orgGardener.tree.selected()
		let parent = this.getParent()
		if (parent) {
			var tree = this.objectSelector.objectGardener.tree
			// #16017 don't do the subtree check for briefing book slides.  This
			// is hacky but the alternative is a config option that would have
			// to flow through EditButtons which is annoying
			if (!this._isSlide()) {
				// #28708 and #13491 If we're copying, it shouldn't matter where
				// we are copying to, that includes itself.
				let allowed = this.options.mode === 'copy'
					|| !isInSubtree(tree, parent.id, this.options.object.id)
				if (!allowed) {
					error = i18n.get('common.move.cannotMoveToSelf', this.options.object.label)
				}
			}
			if (this.options.mode == 'move' && isLastChild(tree, this.options.object.id, parent.id)) {
				error = i18n.get('common.move.samePosition')
			}
		} else {
			error = testMixingReplication(org, this.sourceOrganization, this.options.domainType)
			if (error) {
				error = i18n.get(error)
			}
		}

		let pasteAction = i18n.get('common.' + this.options.mode)
		pasteAction = _.lowerCase(pasteAction)
		let cantPasteText = i18n.get('common.paste.invalid', pasteAction)

		// #31303 if we've chosen to add as a sibling, make sure that position
		// doesn't violate any rules
		if (parent && (this.options.domainType !== 'slide')) {
			let addType = this.objectSelector.objectGardener.getAddType(parent,
				this.options.object)
			if (addType == SIBLING) {
				// the parent of the sibling node
				let siblingParent = this.objectSelector.objectGardener.tree.parent(
					parent)
				if (!this.pasteAllowed(this.options.object, siblingParent)) {
					return cantPasteText
				}
			}
		}

		if (this.options.domainType === 'slide') {
			if (!parent) {
				return i18n.get('common.error.mustSelect.briefing')
			}
			if (!this.pasteAllowed(this.options.object, parent)) {
				return cantPasteText
			}
		}

		return error
	},

	_isSlide: function() {
		return this.options.url && this.options.url.indexOf('slides') != -1
	},

	render: function() {
		this.$el.empty().append(template({
			title: _.result(this, 'title'),
			messages: _.result(this, 'messages')
		}))
		this.$('.object-selector').replaceWith(this.objectSelector.el)
		this.objectSelector.render()

		if (this.options.wizard) {
			this.options.wizard.render({
				formView: this.formView,
				doneKey: function() {
					return _.result(this, 'primaryButtonLabel')
				}
			})
		} else if (this.options.primaryButtonLabel) {
			this.$('.button-primary').text(this.options.primaryButtonLabel)
		}

		this.formView.start()

		return this
	},

	stopListening: function() {
		Backbone.View.prototype.stopListening.apply(this, arguments)
		this.objectSelector.stopListening()
	},

	resize: function(maxHeight) {
		resizeDialogContent({
			dialog: this.el,
			maxHeight,
			target: this.$('.object-selector')
		})
		this.objectSelector.resize()
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.objectSelector, this.formView], 'remove')
	}

})

_.extend(PasteDestinationForm, {
	isLastChild
})
export default PasteDestinationForm
export { isLastChild }

PasteDestinationForm.__filename__ = 'components/paste/paste-destination-form.js'
